import { createContext, useContext, useEffect, useMemo } from "react";
import { useResolveStoreByHostQuery } from "../store/apiSlice";
import { toast } from "react-toastify";
import { themeManager } from "../theme";
import Loader from "../components/utils/Loader";
import { getCookie, setCookie, deleteCookie } from "../utils/cookies";
import { Box } from "@mui/material";
import { AppTypography } from "../components/common/Typography";
import AppButton from "../components/common/Button";

const StoreInfoContext = createContext();

export const useStoreInfo = () => {
  const context = useContext(StoreInfoContext);
  if (!context) {
    toast.error("useStoreInfo must be used within a StoreInfoProvider");
  }
  return context;
};

export const StoreInfoProvider = ({ children }) => {
  const storeIdParam = useMemo(() => {
    if (typeof window === "undefined") return null;
    const params = new URLSearchParams(window.location.search);
    const id = params.get("store_id") ?? params.get("storeId") ?? null;
    return id !== "" ? id : null;
  }, []);

  const cachedSelection = useMemo(() => {
    try {
      const raw = getCookie("storeSelection");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const {
    data: resolvedData,
    isLoading: resolving,
    error: resolveError,
  } = useResolveStoreByHostQuery(storeIdParam, {
    skip: false,
  });

  const resolved = resolvedData?.result || {};

  const resolvedCustomerId = resolved.customer?.id || resolved.customerId;
  const resolvedStoreId =
    resolved.store?.id || resolved.store?.store_id || resolved.storeId;
  const resolvedThemeId = resolved.theme?.id || resolved.themeId;

  const hasStoreInUrl =
    storeIdParam != null && String(storeIdParam).trim() !== "";
  const customerId = hasStoreInUrl
    ? resolvedCustomerId || cachedSelection?.customerId
    : resolvedCustomerId;
  const storeId = hasStoreInUrl
    ? resolvedStoreId || cachedSelection?.storeId
    : resolvedStoreId;
  const themeId = hasStoreInUrl
    ? resolvedThemeId || cachedSelection?.themeId
    : resolvedThemeId;

  const storeInfo = resolved.customer && resolved.store ? resolved : null;

  useEffect(() => {
    if (!hasStoreInUrl && cachedSelection) {
      deleteCookie("storeSelection");
    }
  }, [hasStoreInUrl, cachedSelection]);

  useEffect(() => {
    if (
      hasStoreInUrl &&
      cachedSelection &&
      resolvedCustomerId &&
      (cachedSelection.customerId !== resolvedCustomerId ||
        cachedSelection.storeId !== resolvedStoreId)
    ) {
      deleteCookie("storeSelection");
      window.location.reload();
    }
  }, [hasStoreInUrl, cachedSelection, resolvedCustomerId, resolvedStoreId]);

  useEffect(() => {
    if (resolvedCustomerId && !resolving) {
      const currentCache = (() => {
        try {
          const raw = getCookie("storeSelection");
          return raw ? JSON.parse(raw) : null;
        } catch {
          return null;
        }
      })();
      if (!currentCache || currentCache.storeId !== resolvedStoreId) {
        setCookie(
          "storeSelection",
          JSON.stringify({
            customerId: resolvedCustomerId,
            storeId: resolvedStoreId || null,
            themeId: resolvedThemeId || null,
          }),
          { expires: 7 }
        );
      }
    }
  }, [
    resolvedCustomerId,
    resolvedStoreId,
    resolvedThemeId,
    cachedSelection,
    resolving,
  ]);

  useEffect(() => {
    const storeThemeId = storeInfo?.theme?.id;
    if (storeThemeId && storeThemeId !== themeId) {
      setCookie(
        "storeSelection",
        JSON.stringify({
          customerId,
          storeId: storeId || null,
          themeId: storeThemeId,
        }),
        { expires: 7 }
      );
    }
  }, [storeInfo?.theme?.id, customerId, storeId, themeId]);

  // Keep store_id in URL when we have storeId but URL is missing it (e.g. user landed on / with cache)
  useEffect(() => {
    if (typeof window === "undefined" || !storeId) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("store_id") || params.get("storeId")) return;
    params.set("store_id", String(storeId));
    const newSearch = params.toString();
    const newUrl = `${window.location.pathname}?${newSearch}`;
    window.history.replaceState(null, "", newUrl);
  }, [storeId]);

  // Apply theme settings when store info loads
  useEffect(() => {
    const themeSettings = storeInfo?.theme?.settings;
    if (themeSettings && Object.keys(themeSettings).length > 0) {
      themeManager.setSettings(themeSettings);
      window.dispatchEvent(new CustomEvent("themeUpdated"));
    }
  }, [storeInfo?.theme?.settings]);

  useEffect(() => {
    if (resolveError) {
      const errorMessage =
        resolveError?.data?.message ||
        resolveError?.message ||
        "Failed to resolve store from host";
      toast.error(errorMessage);
    }
  }, [resolveError]);

  const isResolving = resolving && !resolveError;
  const hasMinimalData = !!(customerId && storeId);

  if (isResolving || (!resolveError && !hasMinimalData)) {
    return <Loader />;
  }


  if (resolveError && !cachedSelection) {
    const errorMessage =
      resolveError?.data?.message ||
      resolveError?.message ||
      "Failed to resolve store from host. Please check the URL and try again.";

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          padding: 4,
          textAlign: "center",
        }}
      >
        <AppTypography
          variant="h5"
          sx={{ color: "error.main", mb: 2, fontWeight: 600 }}
        >
          Store Not Found
        </AppTypography>
        <AppTypography variant="body1" sx={{ color: "text.secondary", mb: 3 }}>
          {errorMessage}
        </AppTypography>
        <AppButton
          variant="contained"
          onClick={() => window.location.reload()}
        >
          Retry
        </AppButton>
      </Box>
    );
  }

  const clearStoreSelection = () => {
    deleteCookie("storeSelection");
    window.location.reload();
  };

  return (
    <StoreInfoContext.Provider
      value={{
        storeInfo,
        loading: resolving,
        error: resolveError,
        storeId,
        themeId,
        customerId,
        clearStoreSelection,
      }}
    >
      {children}
    </StoreInfoContext.Provider>
  );
};
